<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

// Order here is the order on screen, and the index drives the sliding chip.
const SEGMENTS = [
  REPLY_EDITOR_MODES.REPLY,
  REPLY_EDITOR_MODES.NOTE,
  REPLY_EDITOR_MODES.REMINDER,
];

const CHIP_PADDING = 16;

// Names match the template refs so the linter can see they are used.
const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorReminderMode = useTemplateRef('wootEditorReminderMode');

const segmentSizes = [
  useElementSize(wootEditorReplyMode),
  useElementSize(wootEditorPrivateMode),
  useElementSize(wootEditorReminderMode),
];

/**
 * When replies are restricted the control reads as a note, matching the
 * behaviour before the reminder segment existed.
 */
const activeMode = computed(() => {
  if (props.disabled && props.mode === REPLY_EDITOR_MODES.REPLY) {
    return REPLY_EDITOR_MODES.NOTE;
  }
  return props.mode;
});

const activeIndex = computed(() => {
  const index = SEGMENTS.indexOf(activeMode.value);
  return index === -1 ? 0 : index;
});

const width = computed(
  () => `${segmentSizes[activeIndex.value].width.value + CHIP_PADDING}px`
);

const translateValue = computed(() => {
  const offset = segmentSizes
    .slice(0, activeIndex.value)
    .reduce((total, size) => total + size.width.value + CHIP_PADDING, 0);
  return `${offset}px`;
});

// Only the reply segment is gated. A restricted channel still allows a private
// note and a reminder, neither of which sends anything to the customer.
const isSegmentDisabled = mode =>
  props.disabled && mode === REPLY_EDITOR_MODES.REPLY;

const onSelect = mode => {
  if (isSegmentDisabled(mode)) return;
  emit('select', mode);
};
</script>

<template>
  <!-- No border utility here on purpose. The control used to be a button, and
  the global button style sets border-style to none, so the border it carried
  was never painted. On a div it would be, which is a visible change. -->
  <div
    class="flex items-center w-auto h-8 p-1 transition-all rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
    role="group"
  >
    <button
      ref="wootEditorReplyMode"
      type="button"
      :disabled="isSegmentDisabled(SEGMENTS[0])"
      class="flex items-center gap-1 px-2 z-20 h-6 disabled:cursor-not-allowed"
      @click="onSelect(SEGMENTS[0])"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </button>
    <button
      ref="wootEditorPrivateMode"
      type="button"
      class="flex items-center gap-1 px-2 z-20 h-6"
      @click="onSelect(SEGMENTS[1])"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>
    <button
      ref="wootEditorReminderMode"
      type="button"
      class="flex items-center gap-1 px-2 z-20 h-6"
      @click="onSelect(SEGMENTS[2])"
    >
      {{ $t('CONVERSATION.REPLYBOX.REMINDER') }}
    </button>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1 transition-all duration-300"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </div>
</template>
